import { Scene } from 'phaser';
import { BossFinalInsightCard } from '../components/BossFinalInsightCard';
import { GAME, SCENES } from '../config/constants';
import {
    deveRegistrarReplay,
    resolverInsightsDaCena
} from '../data/bossFinalSceneRules';
import { getCarHoldMotion } from '../data/bossFinalCarMotion';
import {
    BOSS_FINAL_PHASES,
    getBossFinalFrame
} from '../data/bossFinalTimeline';
import {
    CAR_ORIGIN,
    CAR_SCALE,
    buildBossFinalLayout,
    buildVisualInsights
} from '../data/bossFinalCarLayout';

const BACKGROUND_TEXTURE = 'boss-final-reference-background';
const TIRES_TEXTURE = 'boss-final-tires';
// Bounds of each wheel in the original 1459 x 296 SVG.
const TIRE_FRAMES = [
    { name: 'left-wheel', x: 0, y: 0, width: 298, height: 296 },
    { name: 'right-wheel', x: 1175, y: 0, width: 284, height: 283 }
];
const ARGO_X_TEXTURE = 'boss-final-argo-x';
const ARGO_X_Y = 695;
const ARGO_X_ENTRY_Y = 780;

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const lerp = (start, end, progress) => start + (end - start) * progress;
const easeInOutSine = (value) => (1 - Math.cos(Math.PI * value)) / 2;

function mixPosition (from, to, progress) {
    return {
        x: lerp(from.x, to.x, progress),
        y: lerp(from.y, to.y, progress)
    };
}

export class BossFinalScene extends Scene {
    constructor (key = SCENES.BOSS_FINAL) {
        super(key);
    }

    init (data = {}) {
        this.insights = resolverInsightsDaCena(data, import.meta.env.DEV);
        this.seekMs = Number.isFinite(data.seekMs) ? data.seekMs : 0;
    }

    preload () {
        if (!this.textures.exists(TIRES_TEXTURE)) {
            this.load.svg(TIRES_TEXTURE, 'assets/boss-final-pneus.svg');
        }
        if (!this.textures.exists(ARGO_X_TEXTURE)) {
            this.load.image(ARGO_X_TEXTURE, 'assets/boss-final-argo-x.png');
        }
    }

    create () {
        this.startedAt = this.time.now - this.seekMs;
        this.createPixelBackground();
        const tireTexture = this.textures.get(TIRES_TEXTURE);
        this.tires = TIRE_FRAMES.map(({ name, x, y, width, height }) => {
            if (!tireTexture.has(name)) {
                tireTexture.add(name, 0, x, y, width, height);
            }
            return this.add.image(
                CAR_ORIGIN.x + (201 + x + width / 2) * CAR_SCALE,
                CAR_ORIGIN.y + (600 + y + height / 2) * CAR_SCALE,
                TIRES_TEXTURE,
                name
            ).setOrigin(0.5).setScale(CAR_SCALE).setDepth(100_000).setAlpha(0);
        });
        this.argoX = this.add.image(GAME.CENTER_X, ARGO_X_ENTRY_Y, ARGO_X_TEXTURE)
            .setDisplaySize(360, 53)
            .setDepth(100_001)
            .setAlpha(0);

        this.visualInsights = buildVisualInsights(this.insights);
        this.layout = buildBossFinalLayout(this.visualInsights.length);
        this.cards = this.visualInsights.map(
            (insight, index) => new BossFinalInsightCard(this, insight, index)
        );

        if (deveRegistrarReplay(import.meta.env.DEV)) {
            this.input.keyboard?.on('keydown-R', this.replay, this);
        }
        this.events.once('shutdown', this.cleanup, this);
        this.update(this.time.now);
    }

    update (time) {
        const elapsed = time - this.startedAt;
        const frame = getBossFinalFrame(elapsed, this.insights.length);
        // Shared road motion keeps the wheels aligned with the post-it body.
        const { roadOffset, tireRotation } = getCarHoldMotion(elapsed - frame.durationMs);
        this.renderFrame(frame, roadOffset);
        this.tires.forEach((tire) => tire.setRotation(tireRotation));
    }

    createPixelBackground () {
        if (!this.textures.exists(BACKGROUND_TEXTURE)) {
            const texture = this.textures.createCanvas(
                BACKGROUND_TEXTURE,
                GAME.LARGURA_TOTAL,
                GAME.HEIGHT
            );
            const context = texture.getContext();
            const gradient = context.createRadialGradient(
                GAME.LARGURA_TOTAL / 2,
                GAME.CENTER_Y,
                60,
                GAME.LARGURA_TOTAL / 2,
                GAME.CENTER_Y,
                720
            );
            gradient.addColorStop(0, '#f0ff6a');
            gradient.addColorStop(0.48, '#dcfa4d');
            gradient.addColorStop(1, '#b9dc32');
            context.fillStyle = gradient;
            context.fillRect(0, 0, GAME.LARGURA_TOTAL, GAME.HEIGHT);

            const tile = 32;
            for (let y = 0; y < GAME.HEIGHT; y += tile) {
                for (let x = 0; x < GAME.LARGURA_TOTAL; x += tile) {
                    const value = ((x / tile) * 17 + (y / tile) * 31) % 7;
                    context.fillStyle = value < 3
                        ? 'rgba(255, 255, 175, 0.035)'
                        : 'rgba(91, 131, 20, 0.02)';
                    context.fillRect(x, y, tile, tile);
                }
            }

            context.fillStyle = 'rgba(255, 255, 210, 0.06)';
            context.fillRect(0, 0, GAME.LARGURA_TOTAL, 42);
            context.fillStyle = 'rgba(80, 110, 15, 0.035)';
            context.fillRect(0, GAME.HEIGHT - 52, GAME.LARGURA_TOTAL, 52);
            texture.refresh();
        }

        this.add.image(GAME.CENTER_X, GAME.CENTER_Y, BACKGROUND_TEXTURE)
            .setDisplaySize(GAME.LARGURA_TOTAL, GAME.HEIGHT);
    }

    renderFrame (frame, roadOffset = 0) {
        const tireAlpha = frame.phase === BOSS_FINAL_PHASES.CAR_HOLD ? 1
            : frame.phase === BOSS_FINAL_PHASES.CAR_ASSEMBLY
                ? easeInOutSine(clamp01((frame.phaseProgress - 0.8) / 0.2))
                : 0;
        this.tires.forEach((tire, index) => {
            const { y, height } = TIRE_FRAMES[index];
            tire.setAlpha(tireAlpha).setY(
                CAR_ORIGIN.y + (600 + y + height / 2) * CAR_SCALE + roadOffset
            );
        });
        const argoXProgress = frame.phase === BOSS_FINAL_PHASES.CAR_ASSEMBLY
            ? easeInOutSine(clamp01((frame.phaseProgress - 0.7) / 0.3))
            : frame.phase === BOSS_FINAL_PHASES.CAR_HOLD ? 1 : 0;
        this.argoX
            .setY(lerp(ARGO_X_ENTRY_Y, ARGO_X_Y, argoXProgress))
            .setAlpha(argoXProgress);
        this.cards.forEach((card, index) => {
            const item = this.layout[index];
            const state = this.cardState(frame, item, index);
            card.setTransform({ ...state, y: state.y + roadOffset });
        });
    }

    cardState (frame, item, index) {
        const base = {
            alpha: 0.96,
            scale: 0.92,
            depth: index
        };

        if (
            frame.phase === BOSS_FINAL_PHASES.CLUSTER_HOLD
            || frame.phase === BOSS_FINAL_PHASES.INSIGHT_HIGHLIGHT
        ) {
            if (index === frame.highlightIndex) {
                const progress = easeInOutSine(frame.highlightAmount);
                return {
                    ...mixPosition(item.cluster, { x: GAME.CENTER_X, y: GAME.CENTER_Y }, progress),
                    alpha: lerp(0.96, 1, progress),
                    scale: lerp(base.scale, 3.8, progress),
                    depth: progress > 0 ? 90_000 : index
                };
            }
            return {
                ...base,
                ...item.cluster,
                alpha: index < 72 ? 0.96 : 0,
                depth: index
            };
        }

        if (frame.phase === BOSS_FINAL_PHASES.SCATTER) {
            const delayed = clamp01(frame.phaseProgress * 1.18 - item.order * 0.18);
            const progress = easeInOutSine(delayed);
            const initialAlpha = index < 72 ? 0.96 : 0;
            const finalAlpha = index < 68 ? 0.96 : 0;
            return {
                ...base,
                ...mixPosition(item.cluster, item.scatter, progress),
                alpha: lerp(initialAlpha, finalAlpha, progress),
                depth: index
            };
        }

        if (frame.phase === BOSS_FINAL_PHASES.CAR_ASSEMBLY) {
            const delayed = clamp01(frame.phaseProgress * 1.32 - item.order * 0.32);
            const progress = easeInOutSine(delayed);
            return {
                ...base,
                ...mixPosition(item.scatter, item.car, progress),
                alpha: lerp(index < 68 ? 0.96 : 0, 1, progress),
                scale: lerp(base.scale, 1, progress),
                depth: item.depth
            };
        }

        if (frame.phase === BOSS_FINAL_PHASES.CAR_HOLD) {
            return {
                ...base,
                ...item.car,
                alpha: 1,
                scale: 1,
                depth: item.depth
            };
        }

        return {
            ...base,
            ...item.cluster,
            alpha: 0,
            depth: index
        };
    }

    replay () {
        this.startedAt = this.time.now;
        this.update(this.time.now);
    }

    cleanup () {
        this.input.keyboard?.off('keydown-R', this.replay, this);
        this.cards?.forEach((card) => card.destroy());
        this.cards = [];
        this.argoX = null;
    }
}
